/**
 * How fast it goes out, who it refuses to mail twice, and when it may run at
 * all.
 *
 * These are the settings that decide whether a campaign is a nuisance. The
 * defaults are conservative for a reason measured on the production mirror:
 * 361 999 companies share only 294 505 distinct addresses, so without the
 * shared-address rule 67 494 of them would receive somebody else's letter.
 *
 * The dedupe scope has one dangerous option. Deduplicating by DOMAIN sounds
 * tidier than by address until you count: gmail.com alone is 190 078 companies
 * in the register, so "one letter per domain" would mean one letter for half of
 * Estonia. The option exists because it is the right rule for a scanner
 * audience keyed by real company domains; the warning is there because reaching
 * for it on a register audience is a catastrophe.
 *
 * Three numbers on this tab also decide a fourth that nobody was stating: how
 * long the campaign takes, and which of the two limits is actually doing the
 * limiting. Both are now worked out on screen - see `daysToSend` and
 * `capacityPerDay` in lib.ts.
 */
import { AlertTriangle, Clock, CopyMinus, Gauge, Info } from "lucide-react";
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, TimezoneCombobox } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { Campaign } from "../../../../lib/marketing";
import { capacityPerDay, daysToSend } from "../lib";
import { Field, Note, Panel, num } from "../shared";

/** Clamp a typed number into the range the server schema accepts, so the form
 *  cannot offer a value that will come back as a 400. */
function bounded(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw.replace(/\s/g, ""));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function SendingPanel({
  campaign,
  onChange,
  disabled,
}: {
  campaign: Campaign;
  onChange: (p: Partial<Campaign>) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const domainScopeOnRegister = campaign.dedupeScope === "domain" && campaign.audience?.source === "register";

  const capacity = capacityPerDay(campaign.minGapSeconds);
  // The gap wins whenever it is the tighter of the two. Saying which one binds
  // is the difference between a setting and a number that never applies.
  const gapBinds = capacity < campaign.dailyCap;
  const effectiveDaily = Math.min(campaign.dailyCap, capacity);
  const days = campaign.audienceCount != null ? daysToSend(campaign.audienceCount, effectiveDaily) : null;

  return (
    <div className="space-y-4">
      <Panel
        title={t("marketing.sending.paceTitle")}
        description={t("marketing.sending.paceIntro")}
        icon={<Gauge className="size-4" />}
      >
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <Field label={t("marketing.sending.perRunCap")} hint={t("marketing.sending.perRunCapHint")}>
            <Input
              value={String(campaign.perRunCap)}
              onChange={(e) => onChange({ perRunCap: bounded(e.target.value, 1, 500, campaign.perRunCap) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.sending.dailyCap")} hint={t("marketing.sending.dailyCapHint")}>
            <Input
              value={String(campaign.dailyCap)}
              onChange={(e) => onChange({ dailyCap: bounded(e.target.value, 1, 5000, campaign.dailyCap) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.sending.minGap")} hint={t("marketing.sending.minGapHint")}>
            <Input
              value={String(campaign.minGapSeconds)}
              onChange={(e) => onChange({ minGapSeconds: bounded(e.target.value, 0, 3600, campaign.minGapSeconds) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
        </div>

        {/* What the three settings above actually add up to. */}
        <div className="mt-4 space-y-2 border-t border-border pt-3.5">
          <p className="text-xs text-muted-foreground">
            {t("marketing.sending.effective", {
              perDay: num(Number.isFinite(effectiveDaily) ? effectiveDaily : campaign.dailyCap),
            })}
            {days != null && days > 0 && (
              <>
                {" "}
                {t("marketing.sending.takesDays", { n: num(days), audience: num(campaign.audienceCount ?? 0) })}
              </>
            )}
          </p>
          {gapBinds && (
            <Note tone="info" icon={<Info className="size-3.5" />}>
              {t("marketing.sending.gapBinds", { capacity: num(capacity), cap: num(campaign.dailyCap) })}
            </Note>
          )}
        </div>
      </Panel>

      <Panel
        title={t("marketing.sending.dedupeTitle")}
        description={t("marketing.sending.dedupeIntro")}
        icon={<CopyMinus className="size-4" />}
      >
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label={t("marketing.sending.dedupeScope")} hint={t("marketing.sending.dedupeScopeHint")}>
            <Select
              value={campaign.dedupeScope}
              onValueChange={(v) => onChange({ dedupeScope: v as Campaign["dedupeScope"] })}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">{t("marketing.sending.scope.email")}</SelectItem>
                <SelectItem value="company">{t("marketing.sending.scope.company")}</SelectItem>
                <SelectItem value="domain">{t("marketing.sending.scope.domain")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label={t("marketing.sending.recontact")} hint={t("marketing.sending.recontactHint")}>
            <Input
              value={String(campaign.recontactDays)}
              onChange={(e) => onChange({ recontactDays: bounded(e.target.value, 0, 3650, campaign.recontactDays) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
        </div>

        {domainScopeOnRegister && (
          <Note tone="bad" icon={<AlertTriangle className="size-4" />} className="mt-3.5">
            {t("marketing.sending.domainScopeWarning")}
          </Note>
        )}

        <div className="mt-3.5 space-y-3 border-t border-border pt-3.5">
          <label className="flex items-start gap-2.5 text-sm">
            <Switch
              checked={campaign.skipShared}
              onCheckedChange={(v) => onChange({ skipShared: v })}
              disabled={disabled}
              className="mt-0.5"
            />
            <span>
              {t("marketing.sending.skipShared")}
              <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                {t("marketing.sending.skipSharedHint")}
              </span>
            </span>
          </label>

          <Field label={t("marketing.sending.sharedThreshold")} hint={t("marketing.sending.sharedThresholdHint")}>
            <Input
              value={String(campaign.sharedAddressThreshold)}
              onChange={(e) =>
                onChange({ sharedAddressThreshold: bounded(e.target.value, 2, 1000, campaign.sharedAddressThreshold) })
              }
              inputMode="numeric"
              className="max-w-[10rem]"
              disabled={disabled || !campaign.skipShared}
            />
          </Field>
        </div>
      </Panel>

      <Panel
        title={t("marketing.sending.windowTitle")}
        description={t("marketing.sending.windowIntro")}
        icon={<Clock className="size-4" />}
      >
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <Field label={t("marketing.sending.quietFrom")}>
            <Input
              value={String(campaign.quietFromHour)}
              onChange={(e) => onChange({ quietFromHour: bounded(e.target.value, 0, 23, campaign.quietFromHour) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.sending.quietTo")}>
            <Input
              value={String(campaign.quietToHour)}
              onChange={(e) => onChange({ quietToHour: bounded(e.target.value, 0, 23, campaign.quietToHour) })}
              inputMode="numeric"
              disabled={disabled}
            />
          </Field>
          <Field label={t("marketing.sending.timezone")}>
            <TimezoneCombobox
              value={campaign.timezone}
              onChange={(timezone) => onChange({ timezone })}
              disabled={disabled}
              aria-label={t("marketing.sending.timezone")}
              searchPlaceholder={t("common.timezoneSearch")}
              emptyLabel={t("common.timezoneNone")}
            />
          </Field>
        </div>

        <p className="mt-2.5 text-xs text-muted-foreground">
          {t("marketing.sending.quietSummary", { from: campaign.quietFromHour, to: campaign.quietToHour })}
        </p>

        <label className="mt-3.5 flex items-start gap-2.5 border-t border-border pt-3.5 text-sm">
          <Switch
            checked={campaign.autoEnabled}
            onCheckedChange={(v) => onChange({ autoEnabled: v })}
            disabled={disabled}
            className="mt-0.5"
          />
          <span>
            {t("marketing.sending.autoEnabled")}
            <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
              {t("marketing.sending.autoEnabledHint")}
            </span>
          </span>
        </label>
      </Panel>
    </div>
  );
}
